import { ref } from 'vue';
import router from '@/modules/router';
import { useProductBaseStore } from '@/features/Product/stores/productBase';
import { useProductBundleStore } from '@/features/Product/stores/productBundle';
import { cartActions } from '@/features/Cart/services/cartService';
import { useBundleBuilder } from '@/features/Product/composables/useBundleBuilder';
import { useRandomisation } from '@/features/Product/composables/useRandomisation';
import { useMysteryProduct } from '@/features/Product/composables/useMysteryProduct';
import { resolveBundleCards } from '@/features/Product/composables/bundleCardHelper';
import { useProductStockStore } from '@/features/Product/stores/productStock';
import { useProductUpsellStore } from '@/features/Product/stores/productUpsell';
import { useCartStore } from '@/features/Cart/stores/cart';
import { allUpsells } from '@/features/Product/componentData/productUpsellData';
import { fitsProjectedCart } from '@/features/Cart/services/cartStockSwap';
import { getLineIdentity } from '@/features/Cart/services/lineItemProperties';
import { findVariantParentMaxQuantityFamilies } from '@/features/Cart/cartRules/variantParentMaxQuantity';
import { isBulkProduct } from '@/features/Cart/cartRules/bulkProductMaxQuantity';
import { getCartRuleChanges } from '@/features/Cart/cartRules/cartRuleChanges';
import { getCartStockAdjustments } from '@/features/Cart/services/cartStockAdjustments';
import type { ProductComplete } from '@/features/Product/types/models';
import type { AddToCartItem, ShopifyCart } from '@/features/Cart/types/servicesShopifyCart';
import type { CartUrlActionItem, CartUrlNotice } from '@/features/Cart/types/models';

const cartUrlParams = ['add', 'atc_browse'] as const;
const QUANTITY_PATTERN = /^\d+$/;
const DEFAULT_MAX_CART_QUANTITY = 5;

type CartUrlEntry = {
  identifier: string;
  quantity: number;
  choiceGroupSelection?: string;
};

type CartUrlProductItem = CartUrlActionItem & {
  product: ProductComplete;
};

type CartUrlItemsResolution =
  | { status: 'invalid' }
  | { status: 'unavailable'; titles: string[] }
  | { status: 'ready'; urlItems: CartUrlProductItem[] };

const parseCartUrlEntry = (entry: string): CartUrlEntry | null => {
  const [productPart, choiceGroupSelection, ...extraParts] = entry.split('|');
  const [rawIdentifier = '', rawQuantity, ...extraQuantityParts] = productPart.split(':');
  const identifier = rawIdentifier.trim();
  if (!identifier || extraParts.length || extraQuantityParts.length) return null;
  if (rawQuantity !== undefined && !QUANTITY_PATTERN.test(rawQuantity)) return null;
  const quantity = rawQuantity === undefined ? 1 : Number(rawQuantity);
  if (quantity < 1) return null;
  if (choiceGroupSelection !== undefined && !choiceGroupSelection.trim()) return null;
  return { identifier, quantity, choiceGroupSelection: choiceGroupSelection?.trim() };
};

const parseCartUrlEntries = (value: string): CartUrlEntry[] | null => {
  const entries = value.split(',').map(parseCartUrlEntry);
  const validEntries = entries.filter((entry): entry is CartUrlEntry => !!entry);
  return validEntries.length === entries.length ? validEntries : null;
};

export const useCartUrlParams = () => {
  const productStockStore = useProductStockStore();
  const { isProductSoldOut, refreshStock, getAddableQuantity, getExactStock } = productStockStore;
  const cartStore = useCartStore();
  const { isFreeGiftItem, setCartUrlNotice } = cartStore;

  const hasCartUrlParams = () =>
    cartUrlParams.some((param) => new URLSearchParams(window.location.search).has(param));

  const getProductByIdentifier = (identifier: string) => {
    const productBaseStore = useProductBaseStore();
    const productBundleStore = useProductBundleStore();
    const allProducts = [
      ...productBaseStore.completeBaseProducts,
      ...productBundleStore.completeBundleProducts,
    ];
    return allProducts.find((product) => {
      const sku = product.attributes?.sku;
      const handle = product.attributes?.handle;
      return sku?.toUpperCase() === identifier.toUpperCase() || handle === identifier;
    });
  };

  const getUpsellIdForProduct = (product: ProductComplete): string | undefined => {
    const sku = product.attributes?.sku;
    const handle = product.attributes?.handle;
    const matchingUpsell = allUpsells.find((upsell) => {
      const upsellProduct = upsell.upsellProduct?.product;
      if (!upsellProduct) return false;
      if (typeof upsellProduct === 'string') {
        return upsellProduct === sku || upsellProduct === handle;
      }
      return upsellProduct.attributes?.sku === sku || upsellProduct.attributes?.handle === handle;
    });
    return matchingUpsell?.id;
  };

  const buildCartItem = (
    product: ProductComplete,
    quantity: number,
    choiceGroupSelection?: string,
  ): AddToCartItem | null => {
    const upsellId = getUpsellIdForProduct(product);
    const properties: Record<string, string> = {};
    if (upsellId) {
      properties._upsell_id = upsellId;
    }
    if (product.bundleDefinition) {
      const bundleBuilder = useBundleBuilder({
        bundleDefinition: product.bundleDefinition,
        autoSelectDefaults: !choiceGroupSelection,
      });
      if (choiceGroupSelection) {
        const appliedChoiceGroups = product.bundleDefinition.bundleItems.filter((item, index) => {
          if (item.itemType !== 'choice_group') return false;
          const selectedProduct = item.processedProducts?.find((option) => {
            return (
              option.attributes.sku === choiceGroupSelection ||
              option.attributes.categoryDetails?.typeValue === choiceGroupSelection
            );
          });
          if (!selectedProduct || isProductSoldOut(selectedProduct)) return false;
          const groupKey = item.groupingName || `group${index}`;
          bundleBuilder.clearGroup(groupKey);
          return bundleBuilder.addToGroup(groupKey, selectedProduct, item.quantity || 1);
        });
        if (!appliedChoiceGroups.length) return null;
      }
      const bundleCartObject = bundleBuilder.buildCartObject(product);
      if (!bundleCartObject?.items) return null;
      const { additionalBundleDetails } = product.bundleDefinition;
      const { resolveRandomisationAtcObject } = useRandomisation(
        ref(additionalBundleDetails?.randomisation),
        undefined,
        undefined,
        ref(resolveBundleCards(additionalBundleDetails).map(({ sku }) => sku)),
      );
      const { resolveMysteryAtcObject } = useMysteryProduct(
        ref(additionalBundleDetails?.mysteryProduct),
      );
      const [bundleItem] = resolveMysteryAtcObject(
        resolveRandomisationAtcObject([bundleCartObject.items]),
      );
      if (!bundleItem) return null;
      return {
        ...bundleItem,
        quantity,
        properties: {
          ...bundleItem.properties,
          ...properties,
        },
      };
    }
    return {
      id: product.attributes.variantId,
      quantity,
      ...(Object.keys(properties).length && { properties }),
    };
  };

  const mergeCartUrlItems = (urlItems: CartUrlProductItem[]): CartUrlProductItem[] =>
    urlItems.reduce<CartUrlProductItem[]>((mergedItems, urlItem) => {
      const identity = getLineIdentity(urlItem.item.id, urlItem.item.properties);
      const matchingItem = mergedItems.find(
        ({ item }) => getLineIdentity(item.id, item.properties) === identity,
      );
      if (!matchingItem) return [...mergedItems, urlItem];
      const quantity = matchingItem.item.quantity + urlItem.item.quantity;
      return mergedItems.map((mergedItem) =>
        mergedItem === matchingItem
          ? { ...mergedItem, item: { ...mergedItem.item, quantity } }
          : mergedItem,
      );
    }, []);

  const resolveCartUrlItems = (entries: CartUrlEntry[]): CartUrlItemsResolution => {
    const products = entries.map(({ identifier }) => getProductByIdentifier(identifier));
    if (products.some((product) => !product?.attributes?.variantId)) return { status: 'invalid' };
    const resolvedItems = entries.map(({ quantity, choiceGroupSelection }, index) => {
      const product = products[index] as ProductComplete;
      const title = product.content.title;
      const item = isProductSoldOut(product)
        ? null
        : buildCartItem(product, quantity, choiceGroupSelection);
      return { item, title, product };
    });
    const unavailableTitles = resolvedItems.filter(({ item }) => !item).map(({ title }) => title);
    if (unavailableTitles.length) return { status: 'unavailable', titles: unavailableTitles };
    return {
      status: 'ready',
      urlItems: mergeCartUrlItems(resolvedItems as CartUrlProductItem[]),
    };
  };

  const findUnavailableTitles =
    (urlItems: CartUrlProductItem[]) =>
    (cart: ShopifyCart): string[] => {
      const getCartQuantity = (variantId: number) =>
        cart.items
          .filter((line) => line.variant_id === variantId && !isFreeGiftItem(line))
          .reduce((total, line) => total + line.quantity, 0);
      const limitedTitles = urlItems
        .filter(({ item, product }) => {
          const maxCartQuantity = isBulkProduct(product)
            ? 1
            : Number(item.properties?._max_cart_quantity) ||
              product.attributes.maxCartQuantity ||
              DEFAULT_MAX_CART_QUANTITY;
          const addableQuantity = getAddableQuantity({
            variantId: item.id,
            properties: item.properties,
          });
          return (
            getCartQuantity(item.id) + item.quantity > maxCartQuantity ||
            (addableQuantity !== undefined && addableQuantity < item.quantity)
          );
        })
        .map(({ title }) => title);
      if (limitedTitles.length) return limitedTitles;
      const projectedFit = fitsProjectedCart(
        urlItems.map(({ item }) => item),
        cart,
        { removals: [], additionSkus: urlItems.map(({ product }) => product.attributes.sku) },
        getExactStock,
        findVariantParentMaxQuantityFamilies(),
      );
      return projectedFit === 'fits' ? [] : urlItems.map(({ title }) => title);
    };

  const isRequestedCartSettled =
    (urlItems: CartUrlProductItem[]) =>
    (cart: ShopifyCart): boolean => {
      const requestedIdentities = new Set(
        urlItems.map(({ item }) => getLineIdentity(item.id, item.properties)),
      );
      const isRequestedLine = (key: string) =>
        cart.items.some(
          (line) =>
            line.key === key &&
            requestedIdentities.has(getLineIdentity(line.variant_id, line.properties)),
        );
      const { add, update, remove } = getCartRuleChanges(cart);
      if (add.length || update.length || remove.length) return false;
      const stockAdjustments = getCartStockAdjustments(cart, {
        unavailableLineKeys: productStockStore.unavailableLines,
        protectedLineIdentities: cartStore.latestAddLineIdentities,
        isFreeGiftItem,
        getExactStock,
      });
      if (stockAdjustments.some(({ key }) => isRequestedLine(key))) return false;
      return !useProductUpsellStore().getUpsellCleanupLineKeys().some(isRequestedLine);
    };

  const removeCartUrlParams = async () => {
    const { path, query, hash } = router.currentRoute.value;
    const remainingQuery = Object.fromEntries(
      Object.entries(query).filter(
        ([key]) => !cartUrlParams.some((cartUrlParam) => cartUrlParam === key),
      ),
    );
    await router.replace({ path, query: remainingQuery, hash });
  };

  const showCartNotice = async (notice: CartUrlNotice | null) => {
    if (notice) setCartUrlNotice(notice);
    const { query, hash } = router.currentRoute.value;
    await router.replace({ path: '/cart', query, hash });
  };

  const applyCartUrlParam = async (url: URL): Promise<boolean> => {
    const activeParams = cartUrlParams.filter((param) => url.searchParams.has(param));
    const [activeParam] = activeParams;
    const values = url.searchParams.getAll(activeParam);
    const entries =
      activeParams.length === 1 && values.length === 1 ? parseCartUrlEntries(values[0]) : null;
    if (!entries) {
      await showCartNotice({ type: 'invalid', items: [] });
      return false;
    }
    if (!(await refreshStock({ force: true }))) {
      await showCartNotice({ type: 'not-added', items: [] });
      return false;
    }
    const resolution = resolveCartUrlItems(entries);
    if (resolution.status === 'invalid') {
      await showCartNotice({ type: 'invalid', items: [] });
      return false;
    }
    if (resolution.status === 'unavailable') {
      await showCartNotice({
        type: 'unavailable',
        items: resolution.titles.map((title) => ({ title })),
      });
      return false;
    }
    const { urlItems } = resolution;
    const isAdded = await cartActions().addCartUrlItems(urlItems, {
      findUnavailableTitles: findUnavailableTitles(urlItems),
      isRequestedCartSettled: isRequestedCartSettled(urlItems),
    });
    if (!isAdded) {
      await showCartNotice(null);
      return false;
    }
    if (activeParam !== 'add') return false;
    window.location.replace('/checkout');
    return true;
  };

  const handleCartUrlParams = async () => {
    const url = new URL(window.location.href);
    if (!cartUrlParams.some((param) => url.searchParams.has(param))) return;
    document.documentElement.style.opacity = '0';
    let isLeavingForCheckout = false;
    try {
      await router.isReady();
      await removeCartUrlParams();
      isLeavingForCheckout = await applyCartUrlParam(url);
    } catch (error) {
      console.error('Failed to process cart URL params:', error);
    } finally {
      if (!isLeavingForCheckout) document.documentElement.style.opacity = '1';
    }
  };

  return {
    handleCartUrlParams,
    hasCartUrlParams,
  };
};
